<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :lockable="lockable"
            :has-lock="hasLock"
            :document-lock="documentLock"
            @force-takeover="attemptTakeover"
            @takeover="takeOver"

            :show-preview-button="shouldShowPreviewButton()"
            @click-preview="submitPreviewSite(form.language.iso, form.uri)"

            :errors="errors"
            :is-slide-over="isSlideOver"

            model="App\Models\Page"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <base-page-fields
            class="mt-3"
            :form="form"
            :errors="errors"
            :excluded-types="excludedTypes"
            :editable-timestamps="true"
            :publishable="publishable"
            :h1-anchor-contains-shortcode="true"
        ></base-page-fields>

        <media-reference
            v-model="form.image"
            :language="form.language"
            class="mt-2"
            label="Featured Image"
            :media-types="['Image', 'Screenshot']"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Author</p>
            <reference
                create-text="Create new author and link author to this page"
                link-text="Link an existing author profile"
                two-step-create
                :creatable="false"
                :language="form.language"
                v-model="form.author"
                model-data="Author"
                :should-open-new-window=true
                :excluded-ids="excludedAuthors"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Fact checked by</p>
            <reference
                create-text="Create new author and link author to this page"
                link-text="Link an existing author profile"
                two-step-create
                :creatable="false"
                v-model="form.factChecker"
                model-data="Author"
                :should-open-new-window=true
                :excluded-ids="excludedFactCheckers"
                :order="{
                    'name': 'asc'
                }"
                :language="form.language"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Published For</p>
            <reference
                create-text="Create new country and link country to this page"
                link-text="Link an existing country"
                two-step-create
                :language="form.language"
                v-model="form.publishedFor"
                :should-open-new-window=true
                model-data="Country"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <input-rich-text
            name="overview"
            label="Intro Text (below H1)"
            v-model="form.overview"
            class="mt-2"
            :language="form.language"
            :error="errors.overview ? errors.overview[0] : null"
            :maxlength=5000
        />
        <p class="my-1 text-xs text-gray-600">You can use these available shortcodes in Intro Text (below H1) field: [current_month], [month], [current_year], [year] and [current_abrev_month]</p>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Related Topics</p>
            <multi-reference
                create-text="Create new related topic and link related topic to this page"
                link-text="Link existing related topics"
                :linkable="false"
                v-model="form.related_topics"
                model-data="Related Topic"
                :language="form.language"
                :error="errors.related_topics ? errors.related_topics[0] : null"
            />
        </div>

        <div class="mt-2">
            <span class="text-gray-700">Content Blocks</span>
            <body-content
                :gallery="form.gallery"
                :language="form.language"
                v-model="form.contentBlocks"
                :errors="errors"
                :content-section-with-how-to="contentSectionWithHowTo"
                :content-section-with-bonus-list-top="contentSectionWithBonusListTop"
                :content-section-with-bonus-list-bottom="contentSectionWithBonusListBottom"
                :content-sections-with-ranking="contentSectionsWithRanking"
                :document-class="className"
                :document-id="data ? data.id : null"
                :document-type-id="typeId"
                @add-content-block="addContentBlock"
                @add-content-section="addContentSection"
                @delete-content-block="deleteContentBlock"
                @delete-content-section="deleteContentSection"
                @load-content-blocks="loadContentBlocks"
                @add-how-to="howToAdded"
                @delete-how-to="howToDeleted"
                @add-bonus-list-top="bonusListTopAdded"
                @delete-bonus-list-top="bonusListTopDeleted"
                @add-bonus-list-bottom="bonusListBottomAdded"
                @delete-bonus-list-bottom="bonusListBottomDeleted"
                @add-ranking="rankingAdded"
                @delete-ranking="rankingDeleted"
            ></body-content>
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Sticky CTA</p>
            <reference
                create-text="Create new Sticky CTA and link Sticky CTA to this page"
                link-text="Link existing Sticky CTA"
                v-model="form.stickyCTA"
                :should-open-new-window="true"
                model-data="Sticky-C-T-A"
                :language="form.language"
                :editable="true"
            />
        </div>

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
import pageFormData from "../mixins/pageFormData";
import contentBlocksForm from "../mixins/contentBlocksForm";
import BaseTable from './Table/BaseTable.vue';

export default {
  components: { BaseTable },
    mixins: [crudForm, contentBlocksForm, pageFormData],
    name: "PageForm",
    props: {
        typeId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            form: {
                type_id: this.typeId,
                editor_note: this.getFormData("editor_note"),
                h1_anchor: this.getFormData("h1_anchor"),
                overview: this.getFormData("overview"),
                image_id: this.getFormData("image_id"),
                image: this.getFormData("image"),
                author_id: this.getFormData("author_id"),
                author: this.getFormData("author"),
                fact_checker_id: this.getFormData("fact_checker_id"),
                factChecker: this.getFormData("fact_checker"),
                published_for_id: this.getFormData("published_for_id", this.languageData.default_country_id),
                publishedFor: this.getFormData("published_for", this.languageData.default_country),
                related_topics: this.getFormData("related_topics"),
                stickyCTA: this.getFormData("sticky_c_t_a"),
                sticky_cta_id: this.getFormData("sticky_cta_id"),
                created_at: this.getFormData("created_at") || new Date(),
                created_at_override: this.getFormData("created_at_override"),
                updated_at: this.getFormData("updated_at") || new Date(),
                updated_at_override: this.getFormData("updated_at_override"),
                variants: this.getFormData('variants', []),
            },
        };
    },
    computed: {
        excludedAuthors () {
            return [this.form.fact_checker_id];
        },
        excludedFactCheckers () {
            return [this.form.author_id];
        },
        excludedTypes() {
            switch(this.typeId) {
                case 5: return ['License Main Page'];
                case 6: return ['Payment Method Main Page'];
                case 7: return ['Knowledge Hub Page'];
                case 8: return ['Blog Main Page'];
                case 9: return ['Guide Main Page'];
                default: return [];
            }
        },
    },
    watch: {
        "form.author": function (newValue) {
            this.form.author_id = newValue ? newValue.id : null;
        },
        "form.image": function (newValue) {
            this.form.image_id = newValue ? newValue.id : null;
        },
        "form.factChecker": function (newValue) {
            this.form.fact_checker_id = newValue ? newValue.id : null;
        },
        "form.publishedFor": function (newValue) {
                this.form.published_for_id = newValue ? newValue.id : null;
        },
        "form.stickyCTA": function (newValue) {
            this.form.sticky_cta_id = newValue ? newValue.id : null;
        }
    }
};
</script>
